import { GetStaticPropsContext, InferGetStaticPropsType, NextPage } from 'next';

import {
  AboutFacilitySectionDocument,
  AboutFacilitySectionQuery,
  AboutFacilitySectionQueryVariables,
  AboutHeroDocument,
  AboutHeroQuery,
  AboutHeroQueryVariables,
  AboutImageFeatureDocument,
  AboutImageFeatureQuery,
  AboutImageFeatureQueryVariables,
  AboutLargeImageDocument,
  AboutLargeImageQuery,
  AboutLargeImageQueryVariables,
  AboutNewsroomDocument,
  AboutNewsroomQuery,
  AboutNewsroomQueryVariables,
  AboutSeoDocument,
  AboutSeoQuery,
  AboutSeoQueryVariables,
  AboutSingleTeamMemberSectionDocument,
  AboutSingleTeamMemberSectionQuery,
  AboutSingleTeamMemberSectionQueryVariables,
  FooterDocument,
  FooterQuery,
  FooterQueryVariables,
  FormDocument,
  FormQuery,
  FormQueryVariables,
  NavigationDocument,
  NavigationQuery,
  NavigationQueryVariables,
} from '@/api';
import { addApolloState, initializeApollo } from '@/api/apolloClient';
import { AboutPage } from '@/views/about';

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const client = initializeApollo();
  const { locale } = context;

  //prefetch
  await client.query({ query: AboutSeoDocument });
  await client.query({ query: AboutHeroDocument });
  await client.query({ query: AboutImageFeatureDocument });
  // await client.query({ query: AboutNumbersDocument }); // for future use
  await client.query({ query: AboutFacilitySectionDocument });
  await client.query({ query: AboutSingleTeamMemberSectionDocument });
  await client.query({ query: AboutLargeImageDocument });
  await client.query({ query: AboutNewsroomDocument });
  await client.query({ query: FormDocument });
  await client.query({ query: FooterDocument });
  await client.query({ query: NavigationDocument });

  const { data: seoData } = await client.query<
    AboutSeoQuery,
    AboutSeoQueryVariables
  >({ query: AboutSeoDocument, variables: { locale: locale || 'en' } });

  const { data: heroData } = await client.query<
    AboutHeroQuery,
    AboutHeroQueryVariables
  >({ query: AboutHeroDocument, variables: { locale: locale || 'en' } });

  const { data: imageFeatureData } = await client.query<
    AboutImageFeatureQuery,
    AboutImageFeatureQueryVariables
  >({
    query: AboutImageFeatureDocument,
    variables: { locale: locale || 'en' },
  });

  const { data: facilitySectionData } = await client.query<
    AboutFacilitySectionQuery,
    AboutFacilitySectionQueryVariables
  >({
    query: AboutFacilitySectionDocument,
    variables: { locale: locale || 'en' },
  });

  // for future use
  // const { data: numbersData } = await client.query<
  //   AboutNumbersQuery,
  //   AboutNumbersQueryVariables
  // >({
  //   query: AboutNumbersDocument,
  //   variables: { locale: locale || 'en' },
  // });

  const { data: singleTeamMemberSectionData } = await client.query<
    AboutSingleTeamMemberSectionQuery,
    AboutSingleTeamMemberSectionQueryVariables
  >({
    query: AboutSingleTeamMemberSectionDocument,
    variables: { locale: locale || 'en' },
  });

  const { data: largeImageSectionData } = await client.query<
    AboutLargeImageQuery,
    AboutLargeImageQueryVariables
  >({
    query: AboutLargeImageDocument,
    variables: { locale: locale || 'en' },
  });

  const { data: newsroomData } = await client.query<
    AboutNewsroomQuery,
    AboutNewsroomQueryVariables
  >({
    query: AboutNewsroomDocument,
    variables: { locale: locale || 'en' },
  });

  const { data: formData } = await client.query<FormQuery, FormQueryVariables>({
    query: FormDocument,
    variables: { locale: locale || 'en' },
  });

  const { data: footerData } = await client.query<
    FooterQuery,
    FooterQueryVariables
  >({ query: FooterDocument, variables: { locale: locale || 'en' } });

  const { data: headerData } = await client.query<
    NavigationQuery,
    NavigationQueryVariables
  >({ query: NavigationDocument, variables: { locale: locale || 'en' } });

  return addApolloState(client, {
    props: {
      seoData,
      heroData,
      imageFeatureData,
      // numbersData, // for future use
      facilitySectionData,
      singleTeamMemberSectionData,
      largeImageSectionData,
      newsroomData,
      formData,
      footerData,
      headerData,
    },
  });
};

const AboutUs: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  seoData,
  heroData,
  imageFeatureData,
  // numbersData, // for future use
  facilitySectionData,
  singleTeamMemberSectionData,
  largeImageSectionData,
  newsroomData,
  formData,
  footerData,
  headerData,
}) => {
  return (
    <AboutPage
      seo={seoData?.about?.data?.attributes?.seo}
      hero={heroData?.about?.data?.attributes?.heroImageSection}
      imageFeature={
        imageFeatureData?.about?.data?.attributes?.imageFeatureSection
      }
      // numbers={numbersData?.about?.data?.attributes?.numbersSection} // for future use
      facilitySection={
        facilitySectionData?.about?.data?.attributes?.facilitySection
      }
      singleTeamMemberSection={
        singleTeamMemberSectionData?.about?.data?.attributes
          ?.singleTeamMemberSection
      }
      largeImageSection={
        largeImageSectionData?.about?.data?.attributes?.largeImageSection
      }
      newsroom={newsroomData?.about?.data?.attributes?.newsroom}
      formData={formData}
      footerData={footerData}
      headerData={headerData}
    />
  );
};

export default AboutUs;
